"use client";

import React from "react";
import { ArrowRight, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
export default function JoinTheSquad() {
  return <section data-section-id="120031" id="join-the-eco-squad" className="dark bg-card text-foreground py-24 px-4 relative overflow-hidden flex items-center justify-center min-h-[80vh]">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl pointer-events-none opacity-20" />

      <div className="w-full max-w-2xl mx-auto relative z-10">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-sm font-mono tracking-[0.2em] text-primary uppercase font-bold">
            Sustainability First
          </h2>
          <p className="text-4xl md:text-6xl font-serif uppercase tracking-tight text-foreground">
            Join the Eco-Squad
          </p>
          <p className="text-muted-foreground max-w-md mx-auto">
            Get exclusive access to our organic performance gear and custom team drops.
          </p>
        </div>

        <FormProvider formKey="join_ecosquad_form_home" formId="join_ecosquad_form_home" sectionName="Join The Squad" className="bg-background/40 backdrop-blur-md border border-border/40 p-8 md:p-10 rounded-none shadow-2xl" defaultValues={{
        full_name: "",
        email: "",
        favorite_team: "",
        jersey_size: "",
        marketing_consent: false
      }} validationRules={{
        full_name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        }
      }} submitText="Join the Movement" successMessage="Welcome to the Eco-Squad. Your journey to sustainable performance starts now.">
          <JoinSquadForm />
        </FormProvider>
      </div>
    </section>;
}
function JoinSquadForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center text-primary">
          <CircleCheck className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-serif uppercase tracking-wide text-foreground">
            You're In
          </h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border rounded-full hover:bg-accent hover:text-accent-foreground">
          Register Another Member
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase text-xs tracking-wider font-bold">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Alex Rivera" className="border-0 border-b-2 border-primary/30 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-lg md:text-xl h-auto py-2 transition-colors placeholder:text-muted-foreground/30" {...field} />
              </FormControl>
              <FormDescription className="text-xs">
                Enter your name as you'd like it on custom tags.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase text-xs tracking-wider font-bold">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@ecothread.com" className="border-0 border-b-2 border-primary/30 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-lg md:text-xl h-auto py-2 transition-colors placeholder:text-muted-foreground/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="favorite_team" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground uppercase text-xs tracking-wider font-bold">
                  Your Primary Interest
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <select className="w-full border-0 border-b-2 border-primary/30 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base h-auto py-2 transition-colors appearance-none outline-none text-foreground cursor-pointer" {...field}>
                      <option value="" disabled className="bg-card text-muted-foreground">
                        Select your interest
                      </option>
                      {["Kids Apparel", "Pro Performance", "Fan Gear", "Custom Team Kits", "Eco Accessories"].map((option, i) => <option key={i} value={option} className="bg-card text-foreground" data-index={i}>
                          {option}
                        </option>)}
                    </select>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-primary">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                  </div>
                </FormControl>
                <FormDescription className="text-xs">
                  Help us personalize your eco-experience.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="jersey_size" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground uppercase text-xs tracking-wider font-bold">
                  Preferred Size
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <select className="w-full border-0 border-b-2 border-primary/30 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base h-auto py-2 transition-colors appearance-none outline-none text-foreground cursor-pointer" {...field}>
                      <option value="" disabled className="bg-card text-muted-foreground">
                        Select size
                      </option>
                      {["XS", "S", "M", "L", "XL", "XXL"].map((option, i) => <option key={i} value={option} className="bg-card text-foreground" data-index={i}>
                          {option}
                        </option>)}
                    </select>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-primary">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                  </div>
                </FormControl>
                <FormDescription className="text-xs">
                  Standard athletic fit across all collections.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="marketing_consent" render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
              <FormControl>
                <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-none border border-primary bg-transparent text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background accent-primary cursor-pointer" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-medium cursor-pointer">
                  I’d like to receive eco-updates and custom gear alerts
                </FormLabel>
                <FormDescription className="text-xs">
                  Check to stay in the loop with sustainable releases and promotions.
                </FormDescription>
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-lg font-bold uppercase tracking-widest group relative overflow-hidden bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full transition-all">
        <span className="relative z-10 flex items-center justify-center gap-2">
          {isSubmitting ? <>
              <Loader className="animate-spin size-5" />
              Processing...
            </> : <>
              Join the Movement
              <ArrowRight className="size-5 group-hover:translate-x-2 transition-transform" />
            </>}
        </span>
      </Button>
    </Form>;
}